import type { PlatformRiskCaseSummary } from "@tdelivr/api-client/generated/risk/risk-case.js";
import type { PlatformFraudRuleSetSummary } from "@tdelivr/api-client/generated/risk/fraud-rules.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow, type DetailField } from "./board.ts";

/**
 * What the risk and fraud board does to the two listings it reads, away from any rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `risk`, against **two** operations published
 * for this one page — and against **two refusals** published in the same change, which is what makes
 * this the first board of this console that reaches `degraded` by decision rather than by omission:
 *
 *   - RSK-05 `GET /v1/platform/risk-cases` —
 *     [ADR 0070](../../../docs/adr/0070-a-platform-scoped-risk-case-listing-is-a-separate-operation-and-the-subject-stays-inside.md),
 *     RSK-05's **first public operation that answers anything but its own identity**. Ten fields of
 *     identity and state; the subject under investigation never leaves the service.
 *   - RSK-02 `GET /v1/platform/fraud-rule-sets` —
 *     [ADR 0071](../../../docs/adr/0071-a-fraud-rule-version-header-may-cross-the-tenancy-boundary-and-its-digest-may-not.md),
 *     RSK-02's **first listing at any tenancy bound**. Seven fields of a version header; the rules
 *     and the digest over them stay inside.
 *   - RSK-01 publishes **no listing**, by decision —
 *     [ADR 0072](../../../docs/adr/0072-a-risk-signal-is-personal-data-and-rsk-01-publishes-no-listing.md).
 *     A risk signal is an assertion of suspicion about an identified person, so a cross-tenant
 *     listing of them is a platform-wide register of suspected people on a browser origin.
 *   - RSK-06 publishes **no listing and no evaluation**, by decision —
 *     [ADR 0073](../../../docs/adr/0073-a-blocklist-entry-names-a-person-and-rsk-06-publishes-no-listing-and-no-evaluation.md).
 *     `subject_hash` is a lookup key rather than an anonymisation: an unsalted sha256 of a phone
 *     number over a national numbering plan is seconds of work, and the cheaper attack needs no
 *     inversion at all — a tenant hashes its own customer book and intersects.
 *
 * ```
 * risk:['Risk and fraud','RSK-01…06','Signals, decisions, cases, rules and the blocklist.',
 *   [['Open cases','9','human','to review'],['Blocked today','14','failed','decisions'],
 *    ['Rules','37','done','active'],['Blocklist','212','closed','entries']],
 *   ['Case','Signal','Decision'],
 *   [['RSK-C-2201','5 cards in 10 min','block','human','awaiting analyst'],
 *    ['RSK-C-2198','device shared by 4 accounts','review','human','awaiting analyst'],
 *    ['RSK-C-2190','promo abuse','allow','done','closed']],
 *   'info','Blocklist entries show a reason and an expiry, never just a flag.']
 * ```
 *
 * ## Two of three columns have no source, and that is the measured outcome rather than a shortfall
 *
 * | drawn                                     | source                                                        |
 * | ----------------------------------------- | ------------------------------------------------------------- |
 * | the rows                                  | `RSK-05 GET /v1/platform/risk-cases` (ADR 0070)               |
 * | `Case` — `RSK-C-2201`                     | `riskCases.caseId`, a UUID. The short code is not minted. Q-212 |
 * | `Signal` — `5 cards in 10 min`            | **nothing.** ADR 0072 refuses the listing. Q-210              |
 * | `Decision` — `block`                      | **nothing.** RSK-03's vocabulary, and RSK-03 is not an owner. Q-211 |
 * | `human · awaiting analyst`, `done · closed` | `riskCases.state`                                           |
 * | the source chip's `Rules`                 | `RSK-02 GET /v1/platform/fraud-rule-sets` (ADR 0071)          |
 * | the four KPI tiles                        | **nothing** — a cursor listing answers a page. R-04, Q-178, Q-213 |
 * | the `Add a risk rule` form                | **drawn and refused.** `CREATE_REFUSAL`, ADR 0071 §6          |
 *
 * **`Signal` is left as the design's own `—` and is not synthesised.** RSK-01 holds a
 * `classification` (one of six words), a `severity` (one of four) and a `source` (a producer name);
 * the design's cells are sentences. There is no sentence anywhere in this platform, so even setting
 * ADR 0072's personal-data refusal aside the column would still be empty — ADR 0072 §3's own
 * finding, and the honest answer to "what breaks if we refuse" is "nothing that was going to work".
 * Composing `device` + `high` into "device shared by 4 accounts" is inventing copy, which
 * `CLAUDE.md` §1 forbids.
 *
 * **`Decision` is not filled from `disposition`, although `disposition` is published.** The design's
 * vocabulary is `block | review | allow`, which is RSK-03's; `disposition` is
 * `confirmed_fraud | false_positive | inconclusive`, which is a different enum about a closed
 * investigation rather than a decision taken on a transaction. ADR 0070 §Context says so in those
 * words. A column headed `Decision` showing a disposition would be a wrong answer rather than a
 * missing one — the call `event-catalogue.ts` made when it refused to fill `Consumer` from an
 * event's owning team. It is drawn in the detail panel under its own name instead, which is what
 * the panel is for.
 *
 * ## RSK-02 answers, and what it answers draws no cell
 *
 * The design asks RSK-02 for exactly one thing: the tile `Rules · 37 · done · active`. A cursor
 * listing answers a page and a `nextAfter`, never a total, so the tile has no source either way —
 * ADR 0071's own consequence is that "the board gains no drawable element from this listing".
 *
 * **So the rule sets are not joined onto a case row, and there is nothing to join them with.** A
 * `risk_cases` row carries a `sourceType` and a withheld `source_reference`; it carries no
 * `policyKey`, no rule identifier and no policy version. There is no published relation between a
 * case and a rule set in either direction. A panel line headed `Fraud policy` filled from whichever
 * rule set happened to share the case's tenant would be an assertion the platform did not make, so
 * the listing feeds the one thing it can honestly feed: a sentence under the grid saying what RSK-02
 * answered and that a page is not the tile's total. `ruleSetNote` is that sentence.
 *
 * That makes RSK-02 the secondary listing in the shape `ui/EventCatalogue.tsx` established, and the
 * asymmetry is the same one: **the cases are the grid and a failure there is the board's failure
 * state; the rule sets are one sentence and a failure there degrades the sentence and keeps every
 * row.** One `Promise.all` would hide every risk case on the platform because a second service was
 * unreachable, on a page an operator opens because something is wrong.
 *
 * ## What RSK-05 publishes, and what it deliberately does not
 *
 * Published, ten: `tenantId`, `caseId`, `sourceType`, `priority`, `state`, `disposition`, `version`,
 * `evidenceCount`, `createdAt`, `updatedAt`.
 *
 * Withheld, each for ADR 0070 §5's reason: **`subjectReference`** — the person, device or account
 * under investigation, and a cross-tenant column of it is a register of who is suspected of fraud;
 * **`sourceReference`** — which re-identifies the same person by indirection, so it is withheld
 * beside the subject rather than instead of it; **`assignedAnalyst`, `resolvedBy`, `closedBy`** —
 * three staff subjects under a three-way segregation of duties, which enforcing needs and drawing a
 * badge does not; **`dispositionReason`** — free text an analyst wrote about an identified person;
 * and **every entry of `evidence[]`** — vault addresses of restricted and highly restricted
 * material with their digests, their retention dates and the analyst who attached each one. Only
 * `evidenceCount` crosses, and ADR 0070 §5 keeps even the array out of PostgreSQL's answer rather
 * than reducing it in TypeScript afterwards.
 *
 * None of those absences costs this board a cell. The two empty columns are the two services that
 * published nothing.
 *
 * ## What RSK-02 publishes, and why the digest is not among it
 *
 * Published, seven: `tenantId`, `policyKey`, `version`, `status`, `createdAt`, `publishedAt`,
 * `retiredAt`. Withheld: **`rules`** — the conditions, operators and thresholds *are* the anti-abuse
 * control, and a cross-tenant page of them tells one attacker the exact number to stay under on
 * every tenant at once; and **`artifact_sha256`** — which is not tenant-salted, so two tenants
 * running identical policy have the byte-identical digest and the field is a cross-tenant equality
 * oracle needing no computation at all (ADR 0071 §Context, measured).
 */

/**
 * The scope RSK-05's platform risk-case listing is bought with.
 *
 * A literal in this application's source, which is what grants it: `channel-scopes.mjs` derives
 * `tdelivr-platform-console`'s realm grant from the scope strings this console's own files name, so
 * naming it here is the grant and nothing else is. ADR 0032's mechanism, ADR 0049's rule that a
 * channel names only the scopes its drawn screens spend, and ADR 0070 §2 on why `risk-cases:read` is
 * not widened instead — it buys a tenant-bounded read held by workloads, and
 * `risk-cases:evidence:read` is explicitly not a candidate because it buys an **audited** evidence
 * download and a scope whose purpose is "this access was logged against a named analyst" must not
 * also buy an unlogged cross-tenant page.
 *
 * The literal lives in `navigation.ts`, where all eighteen of this console's cross-boundary
 * scopes are collected so that "what can this console reach" is answerable from one file.
 * Re-exported here so a reader of this module finds it beside the reasoning, and so
 * `channel-scopes.mjs` sees one literal rather than two.
 *
 * **Q-139 takes its seventh occasion here.** Nobody has decided who may hold a scope that reads
 * across every tenant on the platform, and this board names two of them.
 */
export { RISK_CASES_SCOPE } from "./navigation.ts";

/**
 * The scope RSK-02's platform rule-set listing is bought with.
 *
 * ADR 0071 §2. `fraud-rules:read` is **not** widened, and the reason is specific rather than
 * general: it buys the `published-artifact` read, which answers `rules` whole — the one thing this
 * listing exists to withhold. The write, publish and retire scopes each buy a lifecycle command.
 *
 * The literal lives in `navigation.ts` for the same reason `RISK_CASES_SCOPE` does, and both are
 * spent together. Q-139's eighth occasion.
 */
export { FRAUD_RULES_SCOPE } from "./navigation.ts";

/** The states RSK-05's `state` can answer, in the order its own lifecycle reaches them. */
export type RiskCaseState =
  "awaiting analyst" | "assigned" | "investigating" | "resolved" | "closed";

/**
 * The badge's class and word, from `state` alone.
 *
 * **Two of the design's three badges are its own words and three states have no designed row**,
 * which is the split `partner-api.ts` and `event-catalogue.ts` both record: the words the design drew
 * are copied word for word and the states it did not draw take the contract's own word with the
 * nearest of the token system's nine classes.
 *
 * | `state`         | class    | word              | whose word                                    |
 * | --------------- | -------- | ----------------- | --------------------------------------------- |
 * | `open`          | `human`  | `awaiting analyst`| the design's first and second rows            |
 * | `assigned`      | `idle`   | `assigned`        | the contract's                                |
 * | `investigating` | `active` | `investigating`   | the contract's                                |
 * | `resolved`      | `done`   | `resolved`        | the contract's                                |
 * | `closed`        | `done`   | `closed`          | the design's third row, class included        |
 *
 * Four readings there are decisions rather than transcriptions:
 *
 * **`open` is `awaiting analyst`.** The design's two `human · awaiting analyst` rows are cases
 * waiting on a person, and `open` is the one state of the five with no analyst assigned — the token
 * system's own `human` class lists `open` among its examples. `assignedAnalyst` is withheld, so the
 * board can say a case is waiting on an analyst and cannot say which; the word is the design's and
 * the absence is ADR 0070 §5's.
 *
 * **`assigned` is `idle`, not `human`.** The token system's `idle` is "Exists; nothing has happened
 * yet" and lists `assigned` among its examples literally. A case with an assignee who has not begun
 * is not waiting on an unnamed person — it is waiting on a named one, and `beginInvestigation`
 * refuses any actor who is not that assignee. Giving it `human`'s solid badge as well would put the
 * heaviest weight in the design's vocabulary on two of five states and sort them together, which
 * loses the distinction an operator triaging a queue is looking for.
 *
 * **`closed` is `done`, which is the design's own pairing and not the token system's obvious one.**
 * The design's third row is `['RSK-C-2190','promo abuse','allow','done','closed']` — class `done`,
 * word `closed` — although a `closed` class exists in the same file's `SEMK`. `CLAUDE.md` §1 says to
 * copy the design, so `done` it is; and it reads correctly in the token system's own terms, where
 * `done` is "finished work needs no attention" and `closed` is "Ended without success or failure". A
 * closed risk case has been resolved *and* closed by two further actors under a segregation of
 * duties, so it is finished work.
 *
 * **`resolved` shares `done` with `closed` and has its own word.** The design draws no row for it.
 * `done`'s examples include `resolved` exactly, and the two words are different words with different
 * glyph-and-word badges, so nothing is shown by colour alone.
 *
 * `disposition` is **not** in the badge. It is a three-value enum about the outcome of an
 * investigation — `confirmed_fraud | false_positive | inconclusive` — and folding it into the state
 * word would make `closed` mean five things. It is a line of the detail panel under its own name.
 */
export function stateOf(summary: PlatformRiskCaseSummary): {
  classKey: StateClassKey;
  label: RiskCaseState;
  title?: string;
} {
  switch (summary.state) {
    case "open":
      return {
        classKey: "human",
        label: "awaiting analyst",
        title:
          "Ingested and not yet assigned. Which analyst it is waiting for is not published: assignedAnalyst is one of three staff subjects ADR 0070 §5 withholds across the tenancy boundary.",
      };
    case "assigned":
      return {
        classKey: "idle",
        label: "assigned",
        title:
          "An analyst holds it and has not begun. The design has no row for this state; the word is RSK-05's own. beginInvestigation refuses any actor who is not the assignee, and this listing withholds the assignee.",
      };
    case "investigating":
      return {
        classKey: "active",
        label: "investigating",
        title:
          "Under investigation. The design has no row for this state; the word is RSK-05's own.",
      };
    case "resolved":
      return {
        classKey: "done",
        label: "resolved",
        title:
          "Resolved with a disposition, and not yet closed. The reason the analyst gave is withheld: it is free text about an identified person. ADR 0070 §5.",
      };
    case "closed":
      return {
        classKey: "done",
        label: "closed",
        title:
          "Closed by a third actor, who may be neither the assignee nor the resolver. The design's own badge for its closed row is done · closed, which is the class drawn here.",
      };
  }
}

/** The states this listing can answer, in the order `stateOf` decides them. */
export const RISK_CASE_STATES: readonly RiskCaseState[] = [
  "awaiting analyst",
  "assigned",
  "investigating",
  "resolved",
  "closed",
];

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(
  summaries: readonly PlatformRiskCaseSummary[],
): readonly RiskCaseState[] {
  const present = new Set(summaries.map((summary) => stateOf(summary).label));
  return RISK_CASE_STATES.filter((label) => present.has(label));
}

/**
 * The `Signal` cell's reason, which is one of the grid's three columns and is empty on every row.
 *
 * Carried on the cell's `title` because the design has no room for it and the alternatives are both
 * worse: a synthesised sentence is invented copy, and a dropped column silently changes the grid's
 * shape.
 */
const SIGNAL_ABSENT =
  "No source anywhere. RSK-01 publishes no listing at any tenancy bound, by decision: a risk signal is an assertion of suspicion about an identified person, so a cross-tenant page of them would be a platform-wide register of suspected people (ADR 0072). And the column would still be empty if it did — the design's cells are sentences, and RSK-01 holds a classification, a severity and a source, with no sentence anywhere in this platform. Q-210.";

/** The `Decision` cell's reason. RSK-03's vocabulary, and RSK-03 is not an owner of this page. */
const DECISION_ABSENT =
  "No source anywhere. block, review and allow are RSK-03's vocabulary; RSK-03 is not an owner of this page and publishes only an evaluate command, with nothing that reads a decision back. RSK-05's disposition is a different enum about a closed investigation and is not a substitute for it — it is drawn in this panel under its own name. Q-211.";

/** Why the `Case` cell is a UUID where the design writes a six-character code. */
const CASE_WIDTH =
  "The design writes RSK-C-2201; case_id is a UUID and nothing in this platform mints a short code. Written in full rather than truncated into something that resembles the designed code, because a reader would believe the resemblance. ADR 0070 §8, Q-212.";

/**
 * One row of the risk and fraud board.
 *
 * @param summary one risk case, as RSK-05's platform listing answered it.
 */
export function riskCaseRow(summary: PlatformRiskCaseSummary): BoardRow {
  const state = stateOf(summary);

  const disposition: DetailField =
    summary.disposition === null
      ? // Genuinely not set rather than unpublished: a case has no disposition until it is resolved.
        // The design draws `—` itself for a value that is not set, which is the vocabulary used here.
        { label: "Disposition", value: "", absent: true }
      : { label: "Disposition", value: summary.disposition };

  return {
    /*
     * The tenant and the case: RSK-05's primary key and this listing's cursor, `(tenant_id,
     * case_id)`. Prefixed with the tenant because the identifier is unique only within one — ADR
     * 0070 §6 — so a React key that collided would make one tenant's row replace another's on a
     * board whose whole purpose is seeing every tenant's.
     */
    id: `${summary.tenantId}:${summary.caseId}`,
    cells: [
      {
        text: summary.caseId,
        // Mono, which is the design's own rule for this cell reached by a different route: its grid
        // takes Geist Mono for a value matching its prefix list, and `RSK` is on that list. The
        // published value is a UUID, which matches none of them — the same kind of value at a
        // different width, which is what `board.ts` extends `mono` for. Q-196's family.
        mono: true,
        title: CASE_WIDTH,
      },
      { text: "", absent: true, title: SIGNAL_ABSENT },
      { text: "", absent: true, title: DECISION_ABSENT },
    ],
    state,
    /*
     * The design tints a row for its `human` and `failed` classes — `--humanRow` and `--failRow` —
     * and this console's stylesheet carries a rule for the first. No state of this listing is
     * `failed`, so the one tint that exists is the one this board needs: a case awaiting an analyst.
     * The badge beside it carries the glyph and the word either way.
     */
    ...(state.classKey === "human" ? { tinted: true } : {}),
    detail: [
      { label: "Tenant", value: summary.tenantId, mono: true },
      { label: "Case", value: summary.caseId, mono: true },
      /*
       * `sourceType` is published and the design has no column for it, which is the panel's own
       * purpose. Written in RSK-05's own vocabulary rather than prettified: `risk_signal` is the
       * published value, and it says which of the two refused services opened the case — the one
       * fact about the `Signal` column's subject that does cross the boundary.
       */
      { label: "Source type", value: summary.sourceType },
      { label: "Priority", value: summary.priority },
      disposition,
      /*
       * The count, never the array. ADR 0070 §5 reduces `evidence` to `jsonb_array_length(evidence)
       * ::int` in SQL so the vault addresses, their digests, their retention dates and the analyst
       * who attached each one are never read out of PostgreSQL for this operation at all.
       */
      { label: "Evidence", value: String(summary.evidenceCount) },
    ],
    /*
     * The design's `History` block with the two instants a case genuinely has, and its version.
     *
     * That block reads `Created`, `Last changed` and `Version` on every page of every one of these
     * design files, with a person's name beside the first two. RSK-05 records three staff subjects
     * and this listing withholds all three, so these are the instants without the names. Q-107 is
     * where the missing actor on every `History` block lives.
     */
    history: [
      { label: "Created", value: asDay(summary.createdAt) },
      { label: "Last changed", value: asDay(summary.updatedAt) },
      { label: "Version", value: `v${String(summary.version)}` },
    ],
    /** The design's own glyph for this panel, which is `FORMS.risk`'s icon: `shield`. */
    glyph: "shield",
  };
}

/**
 * What RSK-02 answered, as a page rather than as a total.
 *
 * The count is said as "answered N across M tenants, P of them published" and never as a figure
 * under a label meaning "the platform runs this many rules". That is the whole of Q-213 and of ADR
 * 0071 §9: the design's `Rules · 37 · active` tile is a cross-tenant total, and a cursor listing
 * answers a page and a `nextAfter`. A filtered page would be worse than this sentence rather than
 * better — it would give the tile a number that *looked* like a total while being a share of one
 * page, which is the thing ADR 0071's rejected `status` filter names as not to do.
 */
function rulePageSentence(ruleSets: readonly PlatformFraudRuleSetSummary[]): string {
  const published = ruleSets.filter((set) => set.status === "published").length;
  const tenants = new Set(ruleSets.map((set) => set.tenantId)).size;
  return `RSK-02 answered ${String(ruleSets.length)} rule-set version${ruleSets.length === 1 ? "" : "s"} across ${String(tenants)} tenant${tenants === 1 ? "" : "s"}, ${String(published)} of them published; that is the page in hand rather than a platform total, and the rules and their digest are withheld.`;
}

/**
 * What the board owes an operator about the two listings behind it and the two refusals beside them.
 *
 * Four things, and each is said rather than implied because each invites a stronger reading than the
 * data supports:
 *
 *   - **how many tenants the rows came from**, because the page is a board of every risk case on the
 *     platform and cannot show that it is: nothing publishes how many tenants exist — `POST
 *     /v1/tenants` exists and there is no `GET` — so "9 across 5 of 7 tenants" is unsayable and
 *     "risk cases from 3 tenants" is exactly true. `partner-api.ts`'s `tenantSpanNote` and
 *     `platform-audit.ts`'s are the same sentence for the same reason.
 *   - **what RSK-02 answered**, because the page's source chip names it, its rows are nowhere on
 *     screen, and an operator has no other way to tell whether it answered at all. Said as a page
 *     rather than as a total, which is the whole of Q-213: the design's `Rules · 37 · active` tile is
 *     a cross-tenant count and a cursor listing answers a page and a `nextAfter`.
 *   - **that two of the three columns have no source**, which is the one absence a reader cannot
 *     discover by hovering a cell they did not think to hover.
 *   - **that the closing note describes a record this page cannot show.** The design's own footnote
 *     is "Blocklist entries show a reason and an expiry, never just a flag", and RSK-06 publishes
 *     neither a listing nor an evaluation (ADR 0073). The note is drawn anyway, word for word,
 *     because it is the design's copy — so the sentence that says it has no rows behind it belongs
 *     under the grid rather than nowhere.
 *
 * @param summaries the risk cases on the board, or `undefined` before they have arrived.
 * @param ruleSets the rule-set page RSK-02 answered, or `undefined` when it did not answer.
 */
export function ruleSetNote(
  summaries: readonly PlatformRiskCaseSummary[] | undefined,
  ruleSets: readonly PlatformFraudRuleSetSummary[] | undefined,
): string | undefined {
  if (summaries === undefined) return undefined;
  const tenants = new Set(summaries.map((summary) => summary.tenantId)).size;
  if (tenants === 0) return undefined;

  const span = `Risk cases from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}.`;

  const rules =
    ruleSets === undefined
      ? "RSK-02's rule-set listing did not answer, so nothing on this page can say how many fraud rules the platform runs."
      : rulePageSentence(ruleSets);

  const columns =
    "The Signal and Decision columns have no source: a signal names a suspected person, and the Decision vocabulary belongs to a service this page does not own.";

  return `${span} ${rules} ${columns}`;
}

/**
 * Why the designed `Add a risk rule` form is drawn and refused.
 *
 * Its fields are `Rule name`, `Signal`, `Threshold`, `Decision`, `Expires` and `Reason`, and its
 * primary is `Save rule`. Three reasons, and any one is enough:
 *
 *   - **RSK-02's writes are `/internal/v1` only.** Draft, publish and retire all take a `tenantId`
 *     on the path, which a platform board has no value for, and ADR 0071 §6 has the router answer
 *     404 on their platform-shaped paths deliberately. A cross-tenant write would have to decide
 *     which tenant it acted for.
 *   - **Five of its six fields match nothing this service models.** ADR 0071 §Context measured each:
 *     `Signal` offers four prose phrases where `allowedFactFields` has ten dotted field names;
 *     `Threshold` offers `5 in 10 min` where a condition holds a single integer with no window at
 *     all; `Decision` offers `allow|review|block`, which is RSK-03's vocabulary, against RSK-02's
 *     `scoreBasisPoints`; and `Expires` has no counterpart anywhere in the record.
 *   - **`Expires`'s own helper text is about a different service.** "Blocklist entries always need an
 *     expiry" is RSK-06's record, which publishes no public operation of any kind (ADR 0073).
 *
 * Drawn and disabled rather than removed, because `CLAUDE.md` §1 says the add or edit form is not to
 * be removed, and rather than wired to something that would answer 404.
 */
export const CREATE_REFUSAL =
  "RSK-02 publishes draft, publish and retire on /internal/v1 only, which the gateway does not expose, and each takes a tenantId this board has no value for. Five of the form's six fields match nothing the service models: Signal offers four prose phrases against ten dotted fact fields, Threshold offers 5 in 10 min against a single integer with no window, Decision is RSK-03's vocabulary against scoreBasisPoints, and Expires has no counterpart — its own helper text is about RSK-06's blocklist, which publishes nothing public. ADR 0071 §6, ADR 0073.";
